import React from "react";
import classNames from "classnames";
import {
  Dropdown,
  DropdownMenu,
  IconButton,
  Tooltip,
} from "@toddle-edu/ds-web";
import {
  PencilLineOutlined,
  DeleteOutlined,
  DotsHorizontalOutlined,
  ChevronRightOutlined,
  LockOpenOutlined,
  LockFilled,
} from "@toddle-edu/ds-icons";

import ConditionalWrapper from "Utils/ConditionalWrapper";
import DeadlinesPopover from "Projects/components/DeadlinesPopover";
import {
  NOT_SUBMITTED,
  OVERDUE,
  REQUEST_RESUBMISSION_OVERDUE,
  REQUEST_RESUBMISSION,
  SUBMISSION_PENDING,
  TURNED_IN_ON_TIME,
  TURNED_IN_LATE,
  RESUBMISSION_NOT_SUBMITTED,
  SUBMISSION_ITEM_CUSTOM_FIELD_UID,
} from "Projects/Constants/stringConstants";
import { isSubmissionTypeIBDefined } from "Projects/modules/Utils";
import _ from "lodash";
import { formatDate } from "Utils";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import { getUserSortOptions } from "Utils/GetFormattedUserName";

export const canRequestResubmission = ({ status, isReturned }) => {
  return !isReturned && _.includes([TURNED_IN_LATE, TURNED_IN_ON_TIME], status);
};

export const getSubmissionStatusLabelDetails = ({
  t,
  status,
  isTurnedIn,
  turnedInAt,
  isReturned,
  returnedAt,
}) => {
  switch (status) {
    case TURNED_IN_ON_TIME:
      return {
        textColor: "var(--text-primary)",
        tableBgColor: "var(--teal-950)",
        label: "project:turned_in",
        tooltip: t("project:status_with_date", {
          status: t("project:turned_in"),
          date: formatDate({
            date: turnedInAt,
            format: DATE_TIME_CONSTANTS.D_MMM_YYYY_h_mm_a,
          }),
        }),
      };
    case TURNED_IN_LATE:
      return {
        textColor: "var(--text-primary)",
        tableBgColor: "var(--teal-950)",
        label: "project:turned_in_late",
        tooltip: t("project:status_with_date", {
          status: t("project:turned_in_late"),
          date: formatDate({
            date: turnedInAt,
            format: DATE_TIME_CONSTANTS.D_MMM_YYYY_h_mm_a,
          }),
        }),
      };
    case NOT_SUBMITTED:
      return {
        textColor: "var(--text-primary)",
        tableBgColor: "var(--surface-semantic-error)",
        label: "project:not_submitted",
      };
    case OVERDUE:
      return {
        textColor: "var(--text-primary)",
        tableBgColor: "var(--surface-semantic-error)",
        label: "project:overdue",
      };
    case REQUEST_RESUBMISSION_OVERDUE:
      return {
        textColor: "var(--text-primary)",
        tableBgColor: "var(--surface-semantic-error)",
        label: "project:request_resubmission_overdue",
        tooltip: t("project:status_with_date", {
          status: t("project:resubmission_requested"),
          date: formatDate({
            date: returnedAt,
            format: DATE_TIME_CONSTANTS.D_MMM_YYYY_h_mm_a,
          }),
        }),
      };
    case REQUEST_RESUBMISSION:
      return {
        textColor: "var(--text-primary)",
        tableBgColor: "var(--surface-semantic-warning)",
        label: "project:resubmission_requested",
        tooltip: t("project:status_with_date", {
          status: t("project:resubmission_requested"),
          date: formatDate({
            date: returnedAt,
            format: DATE_TIME_CONSTANTS.D_MMM_YYYY_h_mm_a,
          }),
        }),
      };
    case SUBMISSION_PENDING:
      return {
        textColor: "var(--text-secondary)",
        label: "common:pending",
      };
    case RESUBMISSION_NOT_SUBMITTED:
      return {
        textColor: "var(--text-primary)",
        tableBgColor: "var(--surface-semantic-error)",
        label: "project:resubmission_not_submitted",
      };

    default:
      return {
        textColor: "var(--text-primary)",
        label: "NA",
      };
  }
};

export const getTableHeaders = ({ t }) => {
  return [
    {
      key: "title_description",
      columnKey: "title_description",
      value: <div>{t("common:title_and_description")}</div>,
      parent: "root",
      children: [],
      level: 0,
      style: {
        maxWidth: "400px",
      },
    },
    {
      key: "submission",
      columnKey: "submission",
      value: <div>{t("common:submission")}</div>,
      parent: "root",
      children: [],
      level: 0,
    },
    {
      key: "due_on",
      columnKey: "due_on",
      value: <div>{t("common:due_on")}</div>,
      parent: "root",
      children: [],
      level: 0,
      sortType: "toggle",
      entity: "DUE_ON",
    },
    {
      key: "closes_on",
      columnKey: "closes_on",
      value: <div>{t("common:closes_on")}</div>,
      parent: "root",
      children: [],
      level: 0,
      sortType: "toggle",
      entity: "CLOSES_ON",
    },
    {
      key: "assigned",
      columnKey: "assigned",
      value: <div>{t("common:assigned")}</div>,
      parent: "root",
      children: [],
      level: 0,
      alignment: "center",
    },
    {
      key: "turned_in",
      columnKey: "turned_in",
      value: <div>{t("project:turned_in")}</div>,
      parent: "root",
      children: [],
      level: 0,
      style: {
        width: "84px",
      },
      alignment: "center",
    },
    {
      key: "completed",
      columnKey: "completed",
      value: <div>{t("common:completed")}</div>,
      parent: "root",
      children: [],
      level: 0,
      alignment: "center",
    },
    {
      key: "actions",
      columnKey: "actions",
      value: "",
      parent: "root",
      children: [],
      level: 0,
      disableCellClick: true,
      alignment: "right",
    },
  ];
};

export const getTableData = ({
  t,
  deadlines,
  tableHeaders,
  classes,
  projectCount,
  onDropdownOptionClick,
  disabled,
  testId,
}) => {
  const getDropDownOptions = ({ submissionType }) => {
    return [
      {
        key: "EDIT",
        label: t("common:edit"),
        icon: <PencilLineOutlined variant={"subtle"} size={"xx-small"} />,
        disabled,
      },
      {
        key: "DELETE",
        label: t("common:delete"),
        isDestructive: true,
        icon: <DeleteOutlined variant={"subtle"} size={"xx-small"} />,
        disabled: isSubmissionTypeIBDefined({ submissionType }) || disabled,
        disabledTooltip: isSubmissionTypeIBDefined({ submissionType })
          ? t("project:pre_created_deadline_delete_disable_tooltip")
          : null,
      },
    ];
  };

  const tableData = _.map(deadlines, item => {
    const deadline = item?.node;

    const {
      title,
      description,
      id: deadlineId,
      dueOn,
      closesOn,
      submission,
      status: projectGroupDeadlineStatus,
      isStudentSubmissionEnabled,
      turnedInStudents,
      completedStudents,
      turnedInCount: totalTurnedInCount,
      completedCount: totalCompletedCount,
      submissionLockedCount: totalSubmissionLockedCount,
      isDeadlineAllDay,
    } = deadline;

    const { id: submissionId, type: submissionType } = submission || {};

    const rowData = _.map(tableHeaders, header => {
      const { key } = header;
      let value;
      switch (key) {
        case "submission":
          {
            const isSubmissionCompleted = _.isEqual(
              totalSubmissionLockedCount,
              projectCount
            );

            value = isStudentSubmissionEnabled ? (
              isSubmissionCompleted ? (
                <div className={classes.submissionContainer}>
                  <div className={"text-body"}>{t("common:closed")}</div>
                  <LockFilled size="xxx-small" />
                </div>
              ) : (
                <div className={classes.submissionContainer}>
                  <div className={"text-body"}>{t("common:open")}</div>
                  <LockOpenOutlined size="xxx-small" />
                </div>
              )
            ) : (
              <div className={classNames(classes.subtleText, "text-body")}>
                {t("project:na")}
              </div>
            );
          }
          break;
        case "title_description":
          {
            const tooltip = (
              <div
                className={classNames("text-body-s", classes.tooltipContainer)}
              >
                <div>
                  <b>{title}</b>
                </div>
                <div>{description}</div>
              </div>
            );

            value = (
              <Tooltip tooltip={tooltip} placement="bottom">
                <div className={classes.titleDescriptionContainer}>
                  <div
                    className={classNames(classes.titleText, "text-body")}
                    dir="auto"
                  >
                    {title}
                  </div>

                  <div
                    className={classNames(
                      "text-body-s",
                      classes.subtleText,
                      classes.descriptionText
                    )}
                    dir="auto"
                  >
                    {description}
                  </div>
                </div>
              </Tooltip>
            );
          }
          break;
        case "due_on":
          {
            value = dueOn ? (
              <div>
                {formatDate({
                  date: dueOn,
                  format: isDeadlineAllDay
                    ? DATE_TIME_CONSTANTS.D_MMM_YYYY
                    : DATE_TIME_CONSTANTS.D_MMM_YYYY_h_mm_a,
                })}
              </div>
            ) : (
              <div className={classes.subtleText}>
                {t("common:no_with_label", {
                  label: t("common:due_date"),
                })}
              </div>
            );
          }
          break;
        case "closes_on":
          {
            value = closesOn ? (
              <div>
                {formatDate({
                  date: closesOn,
                  format: DATE_TIME_CONSTANTS.D_MMM_YYYY_h_mm_a,
                })}
              </div>
            ) : (
              <div className={classes.subtleText}>
                {t("common:no_with_label", {
                  label: t("common:close_date"),
                })}
              </div>
            );
          }
          break;
        case "turned_in":
          {
            value = projectCount ? (
              submissionId ? (
                <ConditionalWrapper
                  condition={totalTurnedInCount > 0}
                  wrapper={
                    <DeadlinesPopover
                      itemsList={turnedInStudents}
                      title={t("project:turned_in_count_by_total", {
                        count: totalTurnedInCount,
                        total: projectCount,
                      })}
                      popoverType="deadline"
                    />
                  }
                  type={"wrapperAsElement"}
                >
                  <div
                    className={classNames({
                      [classes.hoverText]: totalTurnedInCount > 0,
                    })}
                  >
                    {totalTurnedInCount}
                  </div>
                </ConditionalWrapper>
              ) : (
                <div className={classes.subtleText}>{t("project:na")}</div>
              )
            ) : (
              <div className={classes.subtleText}>-</div>
            );
          }
          break;
        case "assigned":
          {
            value = projectCount ? (
              <div>{projectCount}</div>
            ) : (
              <div className={classes.subtleText}>-</div>
            );
          }
          break;
        case "completed":
          {
            value = projectCount ? (
              totalCompletedCount > 0 ? (
                <ConditionalWrapper
                  condition={totalCompletedCount > 0}
                  wrapper={
                    <DeadlinesPopover
                      itemsList={completedStudents}
                      title={t("project:completed_count_by_total", {
                        count: totalCompletedCount,
                        total: projectCount,
                      })}
                      popoverType="deadline"
                    />
                  }
                  type={"wrapperAsElement"}
                >
                  <div
                    className={classNames({
                      [classes.hoverText]: totalCompletedCount > 0,
                    })}
                  >
                    {totalCompletedCount}
                  </div>
                </ConditionalWrapper>
              ) : (
                <div>{totalCompletedCount}</div>
              )
            ) : (
              <div className={classes.subtleText}>-</div>
            );
          }
          break;
        case "actions": {
          value = (
            <div className={classes.actionContainer}>
              <Dropdown
                dsVersion="2.0"
                overlay={
                  <DropdownMenu
                    shouldStopPropagation={true}
                    dsVersion="2.0"
                    onClick={e => {
                      onDropdownOptionClick({ key: e?.key, deadline });
                      e?.domEvent?.stopPropagation();
                    }}
                    options={getDropDownOptions({ submissionType })}
                    testId={`${testId}-dropdown-${deadlineId}`}
                  />
                }
              >
                <IconButton
                  shouldStopPropagation
                  dsVersion="2.0"
                  variant="neutral"
                  type="plain"
                  icon={<DotsHorizontalOutlined />}
                  testId={`${testId}-actions-${deadlineId}`}
                />
              </Dropdown>

              <ChevronRightOutlined
                dsVersion="2.0"
                size={"xx-small"}
                variant={"subtle"}
                data-test-id={`${testId}-navigate-${deadlineId}`}
              />
            </div>
          );
        }
      }
      return {
        key,
        value,
      };
    });

    return {
      id: deadlineId,
      rowData,
      disableRowClick: projectCount > 0 ? false : true,
    };
  });
  return tableData;
};

export const getCustomDefaultSubmissionItemResponse = ({ items }) => {
  return _.find(
    items,
    item => item?.submissionItem?.fieldUID === SUBMISSION_ITEM_CUSTOM_FIELD_UID
  );
};

export const STUDENT_SORT_OPTIONS = [
  {
    key: "FIRSTNAME_AZ",
    label: "common:first_name_az",
    sortEntity: "FIRST_NAME",
    direction: "asc",
  },
  {
    key: "FIRSTNAME_ZA",
    label: "common:first_name_za",
    sortEntity: "FIRST_NAME",
    direction: "desc",
  },
  {
    key: "MIDDLENAME_AZ",
    label: "common:middle_name_az",
    sortEntity: "MIDDLE_NAME",
    direction: "asc",
  },
  {
    key: "MIDDLENAME_ZA",
    label: "common:middle_name_za",
    sortEntity: "MIDDLE_NAME",
    direction: "desc",
  },
  {
    key: "LASTNAME_AZ",
    label: "common:last_name_az",
    sortEntity: "LAST_NAME",
    direction: "asc",
  },
  {
    key: "LASTNAME_ZA",
    label: "common:last_name_za",
    sortEntity: "LAST_NAME",
    direction: "desc",
  },
  {
    key: "FULLNAME_AZ",
    label: "common:full_name_az",
    sortEntity: "FULL_NAME",
    direction: "asc",
  },
  {
    key: "FULLNAME_ZA",
    label: "common:full_name_za",
    sortEntity: "FULL_NAME",
    direction: "desc",
  },
];

export const getAllStudentSortOptions = ({ t }) => {
  const sortList = _.map(STUDENT_SORT_OPTIONS, type => {
    return {
      ...type,
      label: t(type.label),
    };
  });
  return getUserSortOptions({
    sortOptions: sortList,
    type: "student",
    keyToMap: "sortEntity",
  });
};

export const getStudentSubmissionsForDownload = ({
  studentDetails,
  attachments,
}) => {
  const { id, name } = studentDetails;
  return {
    [id]: {
      attachments,
      folderName: name,
      folderId: id,
    },
  };
};

export const getStudentSubmissionsForBulkDownload = ({
  studentsWithAttachments,
}) => {
  let allStudentsSubmissionObj = {};
  _.map(
    studentsWithAttachments,
    ({ studentDetails = {}, attachments = {} }) => {
      const { id: studentId } = studentDetails;
      const studentSubmissionObj = getStudentSubmissionsForDownload({
        studentDetails,
        attachments,
      });

      allStudentsSubmissionObj = {
        ...allStudentsSubmissionObj,
        [studentId]: studentSubmissionObj[studentId],
      };
    }
  );

  return allStudentsSubmissionObj;
};

export const showSubmissionItem = ({
  hasDynamicSubmissionItems = false,
  fieldUIDs,
  itemFieldUID,
}) => {
  if (hasDynamicSubmissionItems) {
    return _.includes(fieldUIDs, itemFieldUID);
  }
  return true;
};
